import * as Migration from 'shared/migration/migration';
import { Conversion } from 'shared/migration/migration';
import {
  AllFlowsteps,
  ErrorReaction,
  FlowstepType,
  generateFlowstepName,
  generateUuid,
  PullFlowsteps,
  PullMode,
  PushFlowsteps,
  toCategory,
} from 'shared/utils/flowstep';
import { asArray } from 'shared/utils/utils';
import { Flowstep, Operation, ProviderType } from 'shared/types/flowstep';

type LegacyFlowstep = Record<string, any>;

function byType(typeOrTypes: string | string[]) {
  return (flowstep: LegacyFlowstep) => asArray(typeOrTypes).includes(flowstep.type);
}

const conversions: Conversion<LegacyFlowstep>[] = [
  {
    version: `1`,
    rules: [
      { isSuitable: byType(AllFlowsteps), migrateUp: v0Tov1All },
      { isSuitable: byType(PullFlowsteps), migrateUp: v0Tov1Pulls, migrateDown: v1tov0Pulls },
    ],
  },
  {
    version: `2`,
    rules: [
      { isSuitable: byType(FlowstepType.Report), migrateUp: v1tov2Reports },
      { isSuitable: byType(PushFlowsteps), migrateUp: v1tov2Pushes },
      { isSuitable: byType(FlowstepType.Soql), migrateUp: v1tov2Soql },
    ],
  },
  {
    version: `3`,
    rules: [{ isSuitable: byType(PullFlowsteps), migrateUp: v2tov3Pulls }],
  },
  {
    version: `4`,
    shouldMigrate: (flowstep, isMigratingUp) =>
      isMigratingUp ? byType(FlowstepType.Report)(flowstep) : flowstep.provider === ProviderType.SalesforceReport,
    rules: [
      {
        isSuitable: () => true,
        migrateUp: v3tov4Executor,
        migrateDown: v4tov3Report,
      },
    ],
  },
];

export const LEGACY_CURRENT_VERSION = '3';
export const CURRENT_VERSION = '4';

export function migrateToTheLatest(flowsteps: LegacyFlowstep[]) {
  return Migration.migrateToTheLatest(flowsteps, conversions);
}

export function rollbackToLegacy(flowsteps: Flowstep[]) {
  return Migration.migrate(flowsteps, conversions, LEGACY_CURRENT_VERSION);
}

export function migrate(flowsteps: LegacyFlowstep[], toVersion: string | null) {
  return Migration.migrate(flowsteps, conversions, toVersion);
}

function v0Tov1All(flowstep: LegacyFlowstep): LegacyFlowstep {
  const { type, options } = flowstep;
  flowstep.id ??= generateUuid();
  flowstep.name ??= generateFlowstepName({ flowstep });
  flowstep.category = toCategory({ type });

  options.lastUpdateTimestampCell ??= '';
  options.errorReaction ??= ErrorReaction.AskUser;
  return flowstep;
}

function v0Tov1Pulls(flowstep: LegacyFlowstep): LegacyFlowstep {
  const { options, dataTable } = flowstep;
  options.createDataTable = dataTable;
  options.useDropdownsForPicklists ??= false;
  delete flowstep.dataTable;
  return flowstep;
}

function v1tov0Pulls(flowstep: LegacyFlowstep): LegacyFlowstep {
  const { options } = flowstep;
  const { createDataTable } = options;
  delete options.createDataTable;
  flowstep.dataTable = createDataTable;
  return flowstep;
}

function v1tov2Reports(flowstep: LegacyFlowstep): LegacyFlowstep {
  flowstep.options.excludedFields ??= [];
  return flowstep;
}

function v1tov2Pushes(flowstep: LegacyFlowstep): LegacyFlowstep {
  flowstep.options.rangeToApply.nonEmptyColumns ??= [-1];
  return flowstep;
}

function v1tov2Soql(flowstep: LegacyFlowstep): LegacyFlowstep {
  const { options } = flowstep;
  const { object, fields } = options;
  options.object = { name: object?.name };
  options.fields = (fields ?? []).map((x: { name: string }) => ({ name: x.name }));
  options.showLabels ??= false;
  return flowstep;
}

function v2tov3Pulls(flowstep: LegacyFlowstep): LegacyFlowstep {
  flowstep.options.pullMode ??= PullMode.Overwrite;
  return flowstep;
}

function v4tov3Report(flowstep: LegacyFlowstep): LegacyFlowstep {
  const { id, name, flowId, errorReaction, options, providerOptions } = flowstep;
  const {
    target,
    worksheetId,
    pullMode,
    withDateTimeColumn: createDateTimeColumn,
    createDataTable,
    useDropdownsForPicklists,
    recalculateFormulas,
    protectionPassword,
    timestampCell: lastUpdateTimestampCellAddress,
  } = options as Record<string, any>;
  const {
    objectType: report_id,
    objectName: report_name,
    conditions,
    excludedProps: excludedFields,
  } = providerOptions as Record<string, any>;
  const filters = (conditions ?? []).map((c: any) => ({
    name: c.fieldName,
    operator: c.operator,
    value: c.value,
    isSubstitution: c.isSubstitution,
  }));

  return {
    id,
    name,
    flowId,
    type: FlowstepType.Report,
    category: toCategory({ type: FlowstepType.Report }),
    target,
    worksheetId,
    options: {
      errorReaction,
      pullMode,
      createDataTable,
      useDropdownsForPicklists,
      createDateTimeColumn,
      report_id,
      report_name,
      excludedFields,
      filters,
      recalculateFormulas,
      protectionPassword,
      lastUpdateTimestampCellAddress,
    },
  };
}

function v3tov4Executor(flowstep: LegacyFlowstep): Flowstep {
  if (flowstep.type === FlowstepType.Report) {
    const { id, name, flowId, target, worksheetId, options } = flowstep;
    const {
      errorReaction,
      pullMode,
      createDataTable,
      useDropdownsForPicklists,
      createDateTimeColumn,
      report_id,
      report_name,
      excludedFields,
      filters,
      recalculateFormulas,
      protectionPassword,
      lastUpdateTimestampCellAddress,
    } = options;

    return {
      //Version upgrades to 4 automatically in migrration
      version: '3',
      id,
      name,
      flowId,
      errorReaction,
      operation: Operation.PULL,
      provider: ProviderType.SalesforceReport,
      options: {
        target,
        worksheetId,
        pullMode,
        withDateTimeColumn: createDateTimeColumn,
        createDataTable,
        useDropdownsForPicklists,
        recalculateFormulas,
        protectionPassword,
        timestampCell: lastUpdateTimestampCellAddress,
      },
      providerOptions: {
        objectType: report_id,
        objectName: report_name,
        conditions: (filters ?? []).map((f: any) => ({
          fieldName: f.name,
          type: f.type ?? '',
          operator: f.operator,
          value: f.value,
          joinOperator: f.joinOperator ?? 'AND',
          isSubstitution: f.isSubstitution,
        })),
        excludedProps: excludedFields,
      },
    };
  }
  return flowstep as Flowstep;
}
